'use client';

import React, { useMemo, useState } from 'react';
import { formatCurrency } from 'src/utils/formatCurrency/formatCurrency';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import { PulseToggle, Tooltip } from 'src/components/PulseUI';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import {
  MetricLocation,
  ProviderMetricsData,
} from 'src/components/Strategy/Profiles/utils';

interface DisplayOption {
  code: string;
  type: string;
  label: string;
}

interface MetricHeader {
  id: number;
  label?: string;
  description?: string;
  display_options?: DisplayOption[];
  execution_params?: {
    code_set?: {
      label?: string;
    };
  };
}

interface StrategyProfileMetricItemProps {
  header: MetricHeader;
  index: number;
  selectedProvider?: ProviderMetricsData;
  strategyLabel?: string;
  isFreshFaces?: boolean;
  /**
   * Fresh Faces strategies can track treating or referring volume — "billed"
   * only reads correctly for treating (PTRI-211).
   */
  isReferring?: boolean;
  onLocationClick?: (location: MetricLocation) => void;
  renderLocation?: (location: MetricLocation, index: number) => React.ReactNode;
  getMetricValue: (
    stepId: number,
    provider?: ProviderMetricsData
  ) => string | number;
}

const StrategyProfileMetricItem: React.FC<StrategyProfileMetricItemProps> = ({
  header,
  index,
  selectedProvider,
  strategyLabel,
  isFreshFaces = false,
  isReferring = false,
  onLocationClick,
  renderLocation,
  getMetricValue,
}) => {
  const stepId = header.id;

  // Check if this metric has locations data
  const hasLocations = useMemo(
    () => selectedProvider?.metrics_v2?.[stepId]?.locations?.length > 0,
    [selectedProvider, stepId]
  );

  const locations = useMemo(
    () => selectedProvider?.metrics_v2?.[stepId]?.locations || [],
    [selectedProvider, stepId]
  );

  // Determine if toggle should be shown (more than one display option exists)
  const displayOptions = useMemo(
    () => header.display_options || [],
    [header.display_options]
  );
  const hasToggle = displayOptions.length > 1;

  // Toggle state - defaults to first option's code
  const [selectedOptionCode, setSelectedOptionCode] = useState<string>(
    displayOptions[0]?.code || ''
  );

  // Get the selected display option based on toggle selection
  const selectedDisplayOption = useMemo(() => {
    if (hasToggle) {
      return displayOptions.find((opt) => opt.code === selectedOptionCode) || displayOptions[0];
    }
    return displayOptions[0];
  }, [hasToggle, selectedOptionCode, displayOptions]);

  const selectedCode = selectedDisplayOption?.code;

  // If no locations, get the metric value based on selected display option
  const metricValue = useMemo(() => {
    if (hasLocations) return null;

    // If we have a selected code from display_options, use it directly
    if (
      selectedCode &&
      selectedProvider?.metrics_v2?.[stepId]?.[selectedCode] !== undefined
    ) {
      return selectedProvider.metrics_v2[stepId][selectedCode];
    }

    // Fall back to existing getMetricValue behavior
    return getMetricValue(stepId, selectedProvider);
  }, [hasLocations, selectedCode, stepId, selectedProvider, getMetricValue]);

  // Calculate code set label with fallback (metric-level then strategy-level)
  const codeSetLabel = useMemo(
    () => header.execution_params?.code_set?.label || strategyLabel,
    [header.execution_params?.code_set?.label, strategyLabel]
  );

  // Determine tooltip text based on strategy type
  const tooltipText = useMemo(() => {
    if (isFreshFaces) {
      if (hasLocations) {
        return isReferring
          ? 'Location of newly referred volume'
          : 'Location of newly billed volume';
      }
      return isReferring
        ? 'Volume referred in the last 30 days'
        : 'Volume billed in the last 30 days';
    }
    return header.description;
  }, [isFreshFaces, isReferring, hasLocations, header.description]);

  // Determine label
  const label = header.label || `Step ${index}`;

  const isValidLocation = (location: MetricLocation): boolean => {
    return Boolean(location?.type && location?.provider_id && location?.name);
  };

  return (
    <article
      className="self-stretch h-28 px-6 py-5 bg-white rounded-lg border border-neutral-200 flex flex-col justify-center gap-2 overflow-hidden"
      data-testid={`strategy-metric-item-${stepId}`}
      aria-label={`${label} metric`}
    >
      <div className="self-stretch flex justify-between items-center gap-2">
        <div className="flex-1 flex items-center gap-1 min-w-0">
          <span className="text-tertiary text-sm font-normal font-['Inter'] leading-5 line-clamp-1">
            {label}
          </span>
          {codeSetLabel && (
            <>
              <span className="text-tertiary text-sm font-normal" aria-hidden="true">-</span>
              <span className="text-tertiary text-sm font-bold underline truncate">
                {codeSetLabel.trim()}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {hasToggle && displayOptions.length === 2 && (
            <PulseToggle
              value={selectedOptionCode}
              onChange={setSelectedOptionCode}
              options={displayOptions.map((opt) => ({
                label: opt.label,
                value: opt.code,
              }))}
              size="xs"
              className="min-w-36"
              alwaysShowLabels
              data-testid={`metric-volume-toggle-${stepId}`}
              aria-label={`Toggle between ${displayOptions.map((o) => o.label).join(' and ')} view`}
            />
          )}
          {hasToggle && displayOptions.length > 2 && (
            <div className="flex items-center gap-1" role="group" aria-label="Metric display options">
              {displayOptions.map((option, idx) => (
                <React.Fragment key={option.code}>
                  <button
                    type="button"
                    className={`text-[10px] capitalize cursor-pointer px-1 py-0.5 rounded-full transition-colors ${
                      selectedOptionCode === option.code
                        ? 'text-sky-600 font-semibold'
                        : 'text-neutral-600 hover:bg-neutral-100'
                    }`}
                    onClick={() => setSelectedOptionCode(option.code)}
                    data-testid={`metric-option-${stepId}-${option.code}`}
                    aria-pressed={selectedOptionCode === option.code}
                  >
                    {option.label}
                  </button>
                  {idx < displayOptions.length - 1 && (
                    <Icon
                      icon={faCircle}
                      className="text-slate-200 text-[0.2rem]"
                      aria-hidden="true"
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
          {tooltipText && (
            <Tooltip content={tooltipText} position="top">
              <Icon
                icon={faCircleInfo}
                className="text-tertiary hover:text-neutral-600"
                size="lg"
                aria-label={tooltipText}
              />
            </Tooltip>
          )}
        </div>
      </div>

      {/* Metric value or locations */}
      {hasLocations ? (
        <div className="flex flex-col gap-1" role="list" aria-label="Metric locations">
          {locations.map((location, locIndex) => {
            const hasValidData = isValidLocation(location);

            return (
              <div key={locIndex} role="listitem">
                {hasValidData ? (
                  renderLocation ? (
                    renderLocation(location, locIndex)
                  ) : (
                    <button
                      type="button"
                      onClick={() => onLocationClick?.(location)}
                      className="text-sky-900 hover:text-sky-600 text-base font-bold underline cursor-pointer bg-transparent border-none p-0 text-left"
                      aria-label={`View details for ${location.name}`}
                      data-testid={`location-button-${location.provider_id}`}
                    >
                      {location.name}
                    </button>
                  )
                ) : (
                  <span className="text-secondary text-base">
                    {location?.name || 'unavailable'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div
          className="text-primary text-2xl font-extrabold font-['Inter'] leading-8"
          data-testid={`metric-value-${stepId}`}
          aria-label={`${label} value: ${
            selectedDisplayOption?.type === 'currency'
              ? formatCurrency(Number(metricValue) || 0)
              : selectedDisplayOption?.type === 'percent'
                ? `${Math.round(Number(metricValue) <= 1 ? Number(metricValue) * 100 : Number(metricValue))}%`
                : handleNumericValue(metricValue)
          }`}
        >
          {selectedDisplayOption?.type === 'currency'
            ? formatCurrency(Number(metricValue) || 0)
            : selectedDisplayOption?.type === 'percent'
              ? `${Math.round(Number(metricValue) <= 1 ? Number(metricValue) * 100 : Number(metricValue))}%`
              : handleNumericValue(metricValue)}
        </div>
      )}
    </article>
  );
};

export default StrategyProfileMetricItem;
